import { send_request } from "./Request";

/**
 * Thin wrapper around send_request for the Docs resource (/project-service/docs).
 * send_request resolves with the axios response on success and resolves (not rejects)
 * with the axios error on non-401/402 failures, so each call normalizes the outcome
 * into { ok, status, data, conflict } for callers.
 */
const BASE = "project-service/docs";

function normalize(res) {
  const status = res?.status ?? res?.response?.status ?? 0;
  const ok = status >= 200 && status < 300;
  const data = ok ? res?.data : res?.response?.data;
  return { ok, status, data, conflict: status === 409 };
}

export async function listDocs(workspaceId) {
  const query = workspaceId ? { workspaceId } : {};
  return normalize(await send_request(`${BASE}/list`, "", query, "GET"));
}

export async function getDoc(docId) {
  return normalize(await send_request(`${BASE}/${docId}`, "", {}, "GET"));
}

export async function createDoc(payload) {
  return normalize(await send_request(`${BASE}/create`, payload, {}, "POST"));
}

export async function updateDoc(docId, payload) {
  return normalize(await send_request(`${BASE}/${docId}`, payload, {}, "PUT"));
}

export async function moveDoc(docId, payload) {
  return normalize(await send_request(`${BASE}/${docId}/move`, payload, {}, "POST"));
}

export async function duplicateDoc(docId) {
  return normalize(await send_request(`${BASE}/${docId}/duplicate`, {}, {}, "POST"));
}

export async function trashDoc(docId) {
  return normalize(await send_request(`${BASE}/${docId}`, "", {}, "DELETE"));
}

export async function getBacklinks(docId) {
  return normalize(await send_request(`${BASE}/${docId}/backlinks`, "", {}, "GET"));
}

export async function getVersions(docId) {
  return normalize(await send_request(`${BASE}/${docId}/versions`, "", {}, "GET"));
}

export async function restoreVersion(docId, version) {
  return normalize(
    await send_request(`${BASE}/${docId}/versions/${version}/restore`, "", {}, "POST")
  );
}

export async function searchDocs(term) {
  return normalize(await send_request(`${BASE}/search`, "", { q: term }, "GET"));
}

export async function listSkills() {
  return normalize(await send_request(`${BASE}/skills`, "", {}, "GET"));
}

/**
 * Builds a parent->children tree (sorted by position) from a flat list of doc summaries.
 * Returns { roots, byId, childrenOf }.
 */
export function buildDocTree(summaries) {
  const byId = new Map();
  const childrenOf = new Map();
  summaries.forEach((d) => {
    byId.set(d.docId, d);
    const key = d.parentDocId || "__root__";
    if (!childrenOf.has(key)) childrenOf.set(key, []);
    childrenOf.get(key).push(d);
  });
  const sortByPosition = (a, b) =>
    String(a.position || "").localeCompare(String(b.position || ""));
  childrenOf.forEach((arr) => arr.sort(sortByPosition));
  return {
    byId,
    childrenOf,
    roots: childrenOf.get("__root__") || [],
  };
}
